(() => {
  const STORAGE_KEY = 'framewerk_shipping_postcode';
  const ROOT = () => window.Shopify?.routes?.root || '/';
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function endpoint(path) {
    return `${ROOT()}${path}`.replace(/([^:]\/)\/+/g, '$1');
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });

    const text = await response.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {}

    if (!response.ok) {
      throw new Error(
        data?.description || data?.message || text || `Shopify request failed (${response.status})`
      );
    }

    return data;
  }

  function stateForPostcode(postcode) {
    const p = Number(postcode);

    if (p >= 800 && p <= 999) return { name: 'Northern Territory', code: 'NT' };
    if ((p >= 200 && p <= 299) || (p >= 2600 && p <= 2618) || (p >= 2900 && p <= 2920)) return { name: 'Australian Capital Territory', code: 'ACT' };
    if ((p >= 1000 && p <= 2599) || (p >= 2619 && p <= 2899) || (p >= 2921 && p <= 2999)) return { name: 'New South Wales', code: 'NSW' };
    if (p >= 3000 && p <= 3999) return { name: 'Victoria', code: 'VIC' };
    if (p >= 4000 && p <= 4999) return { name: 'Queensland', code: 'QLD' };
    if (p >= 5000 && p <= 5799) return { name: 'South Australia', code: 'SA' };
    if (p >= 6000 && p <= 6797) return { name: 'Western Australia', code: 'WA' };
    if (p >= 7000 && p <= 7999) return { name: 'Tasmania', code: 'TAS' };

    return null;
  }

  // Restored exactly to the original working variant lookup.
  function selectedVariantId(container) {
    const section = container.closest('[id^="shopify-section-"]') || document;
    const input = section.querySelector('form[action*="/cart/add"] [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    return Number(input?.value || container.dataset.variantId || 0);
  }

  function addBusinessDays(start, count) {
    const date = new Date(start);
    let added = 0;

    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) added++;
    }

    return date;
  }

  function etaForPostcode(postcode) {
    const p = Number(postcode);
    let min = 4;
    let max = 6;

    if (p >= 6000 && p <= 6199) [min, max] = [1, 2];
    else if (p >= 6200 && p <= 6797) [min, max] = [2, 4];
    else if (p >= 5000 && p <= 5199) [min, max] = [2, 4];
    else if ((p >= 3000 && p <= 3207) || (p >= 2000 && p <= 2234)) [min, max] = [3, 5];
    else if (p >= 4000 && p <= 4207) [min, max] = [4, 5];
    else if ((p >= 800 && p <= 999) || (p >= 2880 && p <= 2899) || (p >= 4450 && p <= 4499) || (p >= 4700 && p <= 4899)) [min, max] = [5, 7];

    const format = new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });

    return `${format.format(addBusinessDays(new Date(), min))} – ${format.format(addBusinessDays(new Date(), max))}`;
  }

  function money(value) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return String(value || '');

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  }

  // Original temporary-cart-line method retained for manual Shopify/Australia Post rates.
  async function addQuoteLine(variantId, quoteId) {
    const data = await requestJSON(endpoint('cart/add.js'), {
      method: 'POST',
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _framewerk_shipping_quote: quoteId }
        }]
      })
    });

    const line = data?.items?.find((item) => item.properties?._framewerk_shipping_quote === quoteId);
    if (!line?.key) throw new Error('Could not create the temporary shipping quote.');
    return line.key;
  }

  async function removeQuoteLine(lineKey) {
    await requestJSON(endpoint('cart/change.js'), {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  }

  function addressParams(postcode, state) {
    return new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': 'Australia',
      'shipping_address[country_code]': 'AU',
      'shipping_address[province]': state.name,
      'shipping_address[province_code]': state.code
    });
  }

  // Restored exactly to the original working manual-rate request flow.
  async function calculateRates(postcode, state) {
    const params = addressParams(postcode, state);

    await requestJSON(`${endpoint('cart/prepare_shipping_rates.json')}?${params}`, {
      method: 'POST'
    });

    for (let attempt = 0; attempt < 12; attempt++) {
      await sleep(attempt === 0 ? 400 : 700);
      const result = await requestJSON(`${endpoint('cart/async_shipping_rates.json')}?${params}`);

      if (result?.shipping_rates) return result.shipping_rates;
    }

    throw new Error('Shipping rates took too long to calculate. Please try again.');
  }

  function getRememberedPostcode() {
    try {
      const postcode = localStorage.getItem(STORAGE_KEY) || '';
      return /^\d{4}$/.test(postcode) ? postcode : '';
    } catch (_) {
      return '';
    }
  }

  function rememberPostcode(postcode) {
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (_) {}
  }

  function initialise(container) {
    if (container.dataset.framewerkShippingReady === 'true') return;
    container.dataset.framewerkShippingReady = 'true';

    const form = container.querySelector('[data-shipping-form]');
    const input = container.querySelector('[data-shipping-postcode]');
    const button = container.querySelector('[data-shipping-submit]');
    const changeButton = container.querySelector('[data-shipping-change]');
    const loading = container.querySelector('[data-shipping-loading]');
    const error = container.querySelector('[data-shipping-error]');
    const result = container.querySelector('[data-shipping-result]');
    const destination = container.querySelector('[data-shipping-destination]');
    const ratePrice = container.querySelector('[data-shipping-rate-price]');
    const eta = container.querySelector('[data-shipping-eta]');

    if (!input || !button || !loading || !error || !result || !destination || !ratePrice || !eta) {
      console.error('Framewerk shipping calculator: required markup is missing.');
      return;
    }

    const rememberedPostcode = getRememberedPostcode();
    if (rememberedPostcode) input.value = rememberedPostcode;

    let running = false;

    const showError = (message) => {
      error.textContent = message;
      error.hidden = false;
      result.hidden = true;
    };

    const run = async () => {
      if (running) return;

      const postcode = input.value.replace(/\D/g, '').slice(0, 4);
      input.value = postcode;

      if (!/^\d{4}$/.test(postcode)) {
        showError('Please enter a valid 4-digit Australian postcode.');
        return;
      }

      const state = stateForPostcode(postcode);
      const variantId = selectedVariantId(container);

      if (!state) {
        showError('That postcode could not be matched to an Australian state.');
        return;
      }

      if (!variantId) {
        showError('Please select a product option first.');
        return;
      }

      running = true;
      button.disabled = true;
      loading.hidden = false;
      error.hidden = true;
      result.hidden = true;

      const quoteId = `hc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let quoteLineKey = null;

      try {
        quoteLineKey = await addQuoteLine(variantId, quoteId);
        const rates = await calculateRates(postcode, state);

        if (!rates.length) throw new Error('No shipping rates are available for this postcode.');

        // Manual rates are returned by Shopify in their configured order.
        // Use the first returned rate exactly as the original working calculator did.
        const rate = rates[0];

        destination.textContent = postcode;
        ratePrice.textContent = `From ${money(rate.price)}`;
        eta.textContent = etaForPostcode(postcode);
        rememberPostcode(postcode);

        if (form) form.hidden = true;
        result.hidden = false;
      } catch (err) {
        console.error('Framewerk shipping calculator:', err);
        showError(err.message || 'Unable to calculate shipping. Please try again.');
      } finally {
        if (quoteLineKey) {
          try {
            await removeQuoteLine(quoteLineKey);
          } catch (cleanupError) {
            console.error('Could not remove temporary shipping quote:', cleanupError);
          }
        }

        loading.hidden = true;
        button.disabled = false;
        running = false;
      }
    };

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 4);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        run();
      }
    });

    button.addEventListener('click', run);

    if (changeButton) {
      changeButton.addEventListener('click', () => {
        result.hidden = true;
        error.hidden = true;
        if (form) form.hidden = false;
        input.focus();
        input.select();
      });
    }
  }

  function boot(root = document) {
    root.querySelectorAll('[data-framewerk-shipping]').forEach(initialise);
  }

  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
